import { ChangeEvent, useState } from 'react';
import type { SubmitEvent, SyntheticEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import {
  Button,
  Card,
  Skeleton,
  typographyVariants,
} from '@mint-lib/tw-components';

import type { CustomMetric } from '../../api/types.js';
import { useCustomMetricsTypesLabelsByName } from '../../constants/index.js';
import { useCustomMetricsDrawer } from '../../contexts/CustomMetricsContext.jsx';
import { CheckboxWithLabel } from '../atoms/CheckboxWithLabel.jsx';
import { Input } from '../atoms/Input.jsx';
import { Select } from '../atoms/Select.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';
import { DeleteCustomMetricButton } from './DeleteCustomMetricButton.jsx';

type CustomMetricTypeOption = {
  value: CustomMetric['metricType'];
  label: string;
};

export const CustomMetricForm = () => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    isLoading,
    isAddMetricMode,
    selectedMetricDraft,
    updateDraftMetric,
    submitCreateMetric,
    submitUpdateMetric,
  } = useCustomMetricsDrawer();

  const [initialSelectedMetric, setInitialSelectedMetric] =
    useState(selectedMetricDraft);

  const handleChangeCustomMetricLabel = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    updateDraftMetric({ label: event.target.value });
  };

  const customMetricsTypesLabelsByName = useCustomMetricsTypesLabelsByName();
  const customMetricsTypeOptions = Object.entries(
    customMetricsTypesLabelsByName,
  ).map(([value, label]) => ({ value, label }) as CustomMetricTypeOption);

  const handleChangeCustomMetricType = (
    metricTypeOption: (typeof customMetricsTypeOptions)[number],
  ) => {
    updateDraftMetric({ metricType: metricTypeOption.value });
  };

  const handleReset = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (initialSelectedMetric) {
      updateDraftMetric(initialSelectedMetric);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const action = isAddMetricMode ? submitCreateMetric : submitUpdateMetric;
    setIsSubmitting(true);
    try {
      await action();
      setInitialSelectedMetric(selectedMetricDraft);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormPristine =
    selectedMetricDraft.label === initialSelectedMetric?.label &&
    selectedMetricDraft.metricType === initialSelectedMetric?.metricType &&
    selectedMetricDraft.isDateValidationIgnored ===
      initialSelectedMetric?.isDateValidationIgnored;

  const isSubmitDisabled =
    !selectedMetricDraft.label ||
    !selectedMetricDraft.metricType ||
    isFormPristine ||
    isSubmitting;

  const submitButtonText = isAddMetricMode ? t('Add') : t('Update');

  if (isLoading) {
    return <CustomMetricFormSkeleton />;
  }

  return (
    <form onReset={handleReset} onSubmit={handleSubmit} className="flex-1">
      <Card contentClassName="flex flex-col justify-between gap-4 p-5 w-full">
        <h3 className={typographyVariants({ variant: 'h3' })}>
          {isAddMetricMode ? t('Add custom metric') : t('Manage custom metric')}
        </h3>

        <Input
          label={t('Name')}
          value={selectedMetricDraft.label}
          onChange={handleChangeCustomMetricLabel}
          placeholder={t('Enter a custom metric name')}
          maxLength={64}
          autoComplete="off"
        />

        <Select
          label={t('Type')}
          placeholder={t('Select')}
          disabled={!isAddMetricMode}
          options={customMetricsTypeOptions}
          inputValue={
            selectedMetricDraft.metricType
              ? customMetricsTypesLabelsByName[selectedMetricDraft.metricType]
              : ''
          }
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value ?? ''}
          onChange={handleChangeCustomMetricType}
        />
        <div>
          <div className="flex items-center justify-start gap-2">
            <CheckboxWithLabel
              label={t('Import beyond the end date')}
              checked={selectedMetricDraft.isDateValidationIgnored}
              onCheckedChange={(checked: boolean) =>
                updateDraftMetric({
                  isDateValidationIgnored: checked,
                })
              }
            />
            <Tooltip
              description={t(
                `If unchecked, future imports will exclude data beyond the "Date to", but previously imported data will not be deleted, only hidden. Rechecking the box will make it visible again.`,
              )}
            >
              <Information className="fill-icon-secondary" />
            </Tooltip>
          </div>
          <p
            className={typographyVariants({
              variant: 'caption1',
              className: 'mt-2 text-text-secondary',
            })}
          >
            {t(
              `When enabled, 'Date to' validation is skipped, making it possible to import data beyond the end date defined at the selected granularity level.`,
            )}
          </p>
        </div>
      </Card>
      <div className="mt-4 flex justify-between">
        <DeleteCustomMetricButton />
        <div className="flex gap-2">
          <Button
            type="reset"
            variant="text"
            color="primary"
            size="large"
            disabled={isFormPristine}
            data-dd-action-name="Reset"
          >
            {t('Reset')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isSubmitDisabled}
            data-dd-action-name={submitButtonText}
          >
            {submitButtonText}
          </Button>
        </div>
      </div>
    </form>
  );
};

const CustomMetricFormSkeleton = () => {
  return (
    <div className="w-full flex-1">
      <Card
        className="w-full"
        contentClassName="flex flex-col justify-between gap-4 p-5 w-full"
      >
        <Skeleton variant="rounded" className="h-8 w-1/2" />

        <div className="flex flex-col gap-2">
          <Skeleton variant="rounded" className="h-4 w-12" />
          <Skeleton variant="rounded" className="h-10 w-full" />
        </div>

        <div className="flex flex-col gap-2">
          <Skeleton variant="rounded" className="h-4 w-12" />
          <Skeleton variant="rounded" className="h-10 w-full" />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Skeleton variant="rounded" className="h-6 w-6" />
            <Skeleton variant="rounded" className="h-4 w-52" />
            <Skeleton variant="circle" className="h-8 w-8" />
          </div>
          <Skeleton variant="rounded" className="h-8 w-full" />
        </div>
      </Card>

      <div className="mt-4 flex justify-between">
        <Skeleton variant="rounded" className="h-10 w-24" />
        <div className="flex gap-2">
          <Skeleton variant="rounded" className="h-10 w-20" />
          <Skeleton variant="rounded" className="h-10 w-24" />
        </div>
      </div>
    </div>
  );
};
